import axios from 'axios'
import { getCookie } from 'cookies-next'
import { AUTH_JWT_COOKIE } from '@/constants/cookies'

// Recurly base URL for constructing management links
export const RECURLY_BASE_URL = 'https://angel.recurly.com'

export interface CreateRecurlyAccountSubscriptionInput {
  email: string
  token: string
  planCode: string
  userId: string
  /** Optional coupon code (e.g., from a sale promotion) to apply to the subscription */
  couponCode?: string
}

/**
 * Creates a Recurly account subscription via the BFF.
 */
export async function createRecurlyAccountSubscription(input: CreateRecurlyAccountSubscriptionInput): Promise<void> {
  const authToken = getCookie(AUTH_JWT_COOKIE)

  await axios.post(
    `${process.env.NEXT_PUBLIC_BFF_API_URL}/recurly/create-account-subscription`,
    {
      email: input.email,
      token: input.token,
      planCode: input.planCode,
      userId: input.userId,
      // Forward optional coupon to the BFF if provided
      couponCode: input.couponCode,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        ...(typeof authToken === 'string' ? { Authorization: authToken } : {}),
      },
    },
  )
}

/**
 * Retrieves a Recurly hosted login token for the current authenticated user via the BFF.
 * The token can be appended to https://angel.recurly.com/account/ to generate an authenticated management URL.
 */
export async function getRecurlyHostedLoginToken(accountId: string): Promise<string> {
  const authToken = getCookie(AUTH_JWT_COOKIE)

  const response = await axios.get<{ hosted_login_token: string }>(
    `${process.env.NEXT_PUBLIC_BFF_API_URL}/recurly/hosted-login-token`,
    {
      params: { accountId },
      headers: {
        'Content-Type': 'application/json',
        ...(typeof authToken === 'string' ? { Authorization: authToken } : {}),
      },
    },
  )

  return response.data.hosted_login_token
}

/**
 * Returns a fully-qualified, authenticated Recurly account management URL for the given userId.
 * Builds the accountId as `code-${userId}`, fetches a hosted login token, and appends it to the account URL path.
 */
export async function getRecurlyManagementUrlForUser(userId: string): Promise<string> {
  const accountId = `code-${userId}`
  const token = await getRecurlyHostedLoginToken(accountId)
  return `${RECURLY_BASE_URL}/account/${token}`
}

export interface ValidateCouponSuccessResponse {
  valid: true
  coupon: {
    code: string
    name: string
    discountType: 'percent' | 'fixed' | 'free_trial'
    discountPercent: number | null
    discountAmount: number | null
    freeTrialAmount: number | null
    freeTrialUnit: 'day' | 'week' | 'month' | null
    currency: string | null
  }
}

export interface ValidateCouponFailureResponse {
  valid: false
  code: string
  reason?: 'no_applicable_plans' | 'maxed_out'
  message: string
}

export type ValidateCouponResponse = ValidateCouponSuccessResponse | ValidateCouponFailureResponse

export interface CouponValidationResult {
  /** Whether the coupon is valid and synced to Recurly */
  isValid: boolean
  /** Whether PayPal should be supported (false only when coupon valid in Stripe but can't sync to Recurly) */
  isPaypalSupported: boolean
}

/**
 * Validates a promo code via BFF - checks Stripe validity and syncs to Recurly.
 * Server-side only (no auth required).
 */
export async function validateCouponForRecurly(couponCode: string): Promise<CouponValidationResult> {
  try {
    const response = await axios.post<ValidateCouponResponse>(
      `${process.env.NEXT_PUBLIC_BFF_API_URL}/recurly/validate-coupon`,
      { couponCode },
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 5000,
      },
    )

    if (response.data.valid) {
      // Coupon valid in Stripe AND synced to Recurly
      return { isValid: true, isPaypalSupported: true }
    }

    // Coupon invalid - check reason
    if (response.data.reason === 'no_applicable_plans') {
      // Valid in Stripe but can't sync to Recurly - hide PayPal
      return { isValid: false, isPaypalSupported: false }
    }

    // Other failures (not_found, inactive, expired, maxed_out, etc.) - show both options
    return { isValid: false, isPaypalSupported: true }
  } catch (error) {
    // On network/server error, be conservative - show both options
    return { isValid: false, isPaypalSupported: true }
  }
}

/**
 * Error response from the create-account-subscription endpoint.
 * Supports both old and new BFF response formats for backwards compatibility.
 *
 * Old format: { message: "Generic error", error: "Customer-friendly message" }
 * New format: { message: "Customer-friendly message", error: "Customer-friendly message", errorType: "transaction_error" }
 */
export interface RecurlySubscriptionErrorResponse {
  message: string
  error?: string // Customer-friendly message (present in both old and new formats)
  errorType?: string // New format only - categorizes the error type
}

/**
 * Error types returned by the new BFF error format.
 */
export type RecurlyErrorType =
  | 'transaction_error' // Card declined, insufficient funds, payment failures
  | 'validation_error' // Invalid address, missing fields
  | 'client_error' // Other 4xx errors from Recurly
  | 'server_error' // 5xx errors (message will be generic)
  | 'unknown_error' // Unexpected errors

/**
 * Extracts the customer-displayable error message from a Recurly API error response.
 * Handles both old and new BFF response formats for backwards compatibility.
 *
 * @param error - The caught error from createRecurlyAccountSubscription
 * @param fallbackMessage - Message to return if no specific message can be extracted
 * @returns The customer-friendly error message
 */
export function extractRecurlyErrorMessage(error: unknown, fallbackMessage: string): string {
  if (!axios.isAxiosError(error) || !error.response?.data) {
    return error instanceof Error ? error.message : fallbackMessage
  }

  const data = error.response.data as RecurlySubscriptionErrorResponse

  // New format: errorType is present, message is customer-friendly
  if (data.errorType) {
    return data.message
  }

  // Old format: error field has the customer-friendly message, message is generic
  return data.error || data.message || fallbackMessage
}

/**
 * Extracts the error type from a Recurly API error response (new format only).
 * Returns undefined for old format responses.
 */
export function extractRecurlyErrorType(error: unknown): RecurlyErrorType | undefined {
  if (!axios.isAxiosError(error) || !error.response?.data) {
    return undefined
  }

  const data = error.response.data as RecurlySubscriptionErrorResponse
  return data.errorType as RecurlyErrorType | undefined
}

export default createRecurlyAccountSubscription
